import axios from 'axios';
import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

export interface ExistingRegistration {
  uuid: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  [key: string]: any;
}

/**
 * The registrations already attached to a signed-in account, and the choice between
 * reusing one and filling the form in again.
 *
 * The tour arrives from Blade as a JSON string, so it is parsed here rather than by
 * every caller.
 */
export function useAccountRegistrations(
  tour: MaybeRefOrGetter<string | Record<string, any> | null>,
  locale: MaybeRefOrGetter<string>,
) {
  const registrations = ref<ExistingRegistration[]>([]);
  const selectedUuid = ref<string | null>(null);
  const showForm = ref(false);
  const isGuest = ref(false);

  // Choosing to continue as a guest goes straight to the form.
  watch(isGuest, (guest) => {
    showForm.value = guest;
  });

  const tourSlug = (): string | null => {
    const value = toValue(tour);

    if (!value) {
      return null;
    }

    try {
      return (typeof value === 'string' ? JSON.parse(value) : value)?.slug ?? null;
    } catch {
      return null;
    }
  };

  const fetch = async (): Promise<void> => {
    const slug = tourSlug();

    if (!slug) {
      return;
    }

    try {
      await axios.get('/sanctum/csrf-cookie');
      const { data } = await axios.get(`/api/public/${slug}/registration?locale=${toValue(locale)}`);

      registrations.value = data.data;
    } catch (error) {
      console.error('Fetch Data Error:', error);
    }

    // With nothing to reuse, there is no choice to offer.
    if (registrations.value.length === 0) {
      createNew();
    }
  };

  const createNew = (): void => {
    showForm.value = true;
    selectedUuid.value = null;
  };

  const selectExisting = (): void => {
    showForm.value = false;
  };

  const selected = (): ExistingRegistration | undefined =>
    registrations.value.find(registration => registration.uuid === selectedUuid.value);

  return {
    registrations,
    selectedUuid,
    showForm,
    isGuest,
    fetch,
    createNew,
    selectExisting,
    selected,
  };
}
